"use client"

import { usePathname } from "next/navigation"
import { Meeting } from "@/schema"
import { formatDistanceToNow } from "date-fns"
import { Bot } from "lucide-react"

import { useNextMeeting } from "@/hooks/api/meetings"
import { Link } from "@/components/book-link"
import PeekView from "@/components/peek-view"

// Helper function to humanize snake_case text
function humanizeStatus(status: string): string {
  return status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
}

// Helper function to determine status indicator styles
function getStatusIndicatorStyles(status: string): { dotColorClasses: string; pulsingDot: boolean } {
  const lowerStatus = status?.toLowerCase()

  if (lowerStatus === "active" || lowerStatus === "recording" || lowerStatus?.includes("record")) {
    return { dotColorClasses: "bg-green-accent", pulsingDot: true }
  }

  if (lowerStatus === "error" || lowerStatus?.includes("error")) {
    return { dotColorClasses: "bg-red-foreground", pulsingDot: false }
  }

  if (lowerStatus === "processing" || lowerStatus === "connecting") {
    return { dotColorClasses: "bg-yellow-foreground", pulsingDot: true }
  }

  return { dotColorClasses: "bg-icon-muted", pulsingDot: false }
}

// Component for the status indicator dot
function StatusIndicator({ dotColorClasses, pulsingDot }: { dotColorClasses: string; pulsingDot: boolean }) {
  return (
    <span className="relative flex h-2 w-2">
      {pulsingDot && (
        <span
          className={`absolute inline-flex h-full w-full animate-ping rounded-full ${dotColorClasses} opacity-75`}
        />
      )}
      <span className={`relative inline-flex h-2 w-2 rounded-full ${dotColorClasses}`} />
    </span>
  )
}

// Component for notetaker status display
function NotetakerStatus({ notetaker }: { notetaker: { provider_state?: string; status: string } }) {
  const rawStatus =
    "provider_state" in notetaker && notetaker.provider_state ? notetaker.provider_state : notetaker.status

  const statusText = humanizeStatus(rawStatus)
  const { dotColorClasses, pulsingDot } = getStatusIndicatorStyles(rawStatus)

  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-2">
        <StatusIndicator dotColorClasses={dotColorClasses} pulsingDot={pulsingDot} />
        <Bot className="text-muted-foreground h-4 w-4" />
        <span className="text-muted-foreground text-sm">{statusText}</span>
      </div>
    </div>
  )
}

// Component for meeting time display
function MeetingTimeDisplay({ isActive, timeText }: { isActive: boolean; timeText: string }) {
  return (
    <div className="text-muted-foreground flex items-center justify-end gap-1.5 text-xs">
      {isActive && <StatusIndicator dotColorClasses="bg-green-accent" pulsingDot={true} />}
      {timeText}
    </div>
  )
}

// Component for the meeting prep button display
function MeetingPrepDisplay({
  meeting,
  meetingUrl,
  isActive,
  timeText,
}: {
  meeting: Meeting
  meetingUrl: string
  isActive: boolean
  timeText: string
}) {
  return (
    <div className="flex items-center gap-3">
      <PeekView recordType="Meeting" recordId={meeting.id}>
        <Link href={meetingUrl} className="text-right">
          <div className="hover:text-foreground text-sm font-medium underline decoration-dotted underline-offset-4 transition-colors">
            {meeting.title || "Untitled meeting"}
          </div>
          <MeetingTimeDisplay isActive={isActive} timeText={timeText} />
        </Link>
      </PeekView>
    </div>
  )
}

// Main component
export function PrepMeetingButton() {
  const { data: nextMeeting, isLoading } = useNextMeeting()
  const pathname = usePathname()

  if (isLoading || !nextMeeting) {
    return null
  }

  const meetingUrl = `/meetings/${nextMeeting.id}`
  const isOnMeetingPage = pathname === meetingUrl

  const meetingStartTime = nextMeeting?.start_time
  const startTime = new Date(meetingStartTime)
  const now = new Date()
  const isActive = startTime <= now

  const timeDistance = formatDistanceToNow(startTime, { addSuffix: false })
  const timeText = isActive ? "In progress" : `Starts in ${timeDistance}`

  // If we're on the meeting page, show notetaker status instead
  if (isOnMeetingPage && nextMeeting.notetaker) {
    return <NotetakerStatus notetaker={nextMeeting.notetaker} />
  }

  // Otherwise show the prep meeting button
  return <MeetingPrepDisplay meeting={nextMeeting} meetingUrl={meetingUrl} isActive={isActive} timeText={timeText} />
}
